"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.loadProfile = loadProfile;
exports.requireProfile = requireProfile;
/**
 * Profiles: everything the engine knows about one trainer model throwing a
 * ball at a venue, built from the v1 tables and the ball and venue
 * registries, checked once, frozen, and cached.
 */
const models_1 = require("../models");
const tableLimits_1 = require("../tableLimits");
const tableRPM2MPH_1 = require("../tableRPM2MPH");
const tableSpinAdjust_1 = require("../tableSpinAdjust");
const tableTrainerGeometry_1 = require("../tableTrainerGeometry");
const balls_1 = require("./balls");
const freeze_1 = require("./freeze");
const mechanism_1 = require("./mechanism");
const venues_1 = require("./venues");
/** Profiles on the default venues, and per venue a caller passed, by model, sport and ball. */
const defaultVenueCache = new Map();
const venueCache = new WeakMap();
function cacheFor(venue) {
    if (venue === undefined) {
        return defaultVenueCache;
    }
    let cache = venueCache.get(venue);
    if (cache === undefined) {
        cache = new Map();
        venueCache.set(venue, cache);
    }
    return cache;
}
/** The profile for a model, sport and (optionally) venue and ball, or why there is none. */
function loadProfile(model, sport, options = {}) {
    const ball = options.ball ?? ((0, models_1.isSport)(sport) ? balls_1.DEFAULT_BALL[sport] : "");
    const cache = cacheFor(options.venue);
    const key = `${model}/${sport}/${ball}`;
    let result = cache.get(key);
    if (result === undefined) {
        result = (0, freeze_1.deepFreeze)(buildProfile(model, sport, ball, options.venue));
        cache.set(key, result);
    }
    return result;
}
/** The profile for a model, sport and (optionally) venue and ball; throws if unsupported. */
function requireProfile(model, sport, options) {
    const result = loadProfile(model, sport, options);
    if (!result.ok) {
        throw new Error(result.message);
    }
    return result.profile;
}
function buildProfile(model, sport, ballId, requestedVenue) {
    const unsupported = (why) => ({
        ok: false,
        reason: "unsupported",
        message: `${model} / ${sport}: ${why}`,
    });
    if (!Object.hasOwn(tableRPM2MPH_1.tableRPM2MPHMap, model)) {
        return unsupported("unknown physics model");
    }
    if (!(0, models_1.isSport)(sport)) {
        return unsupported("unknown sport");
    }
    const mphByRpm = tableRPM2MPH_1.tableRPM2MPHMap[model][sport];
    if (mphByRpm.length === 0) {
        return unsupported("the model has no RPM-to-speed data for this sport");
    }
    const ball = (0, balls_1.ballById)(ballId);
    if (ball === undefined) {
        return unsupported(`unknown ball ${ballId}`);
    }
    const venue = requestedVenue === undefined ? (0, venues_1.defaultVenue)(sport) : frozenCopy(requestedVenue);
    if (venue === undefined) {
        return unsupported("the venue must be plain data");
    }
    const { RPM: rpmLimits } = tableLimits_1.tableLimitsMap[model][sport];
    const adjust = tableSpinAdjust_1.tableSpinAdjustMap[model][sport];
    const v1Geometry = tableTrainerGeometry_1.tableTrainerGeometryMap[model];
    const constants = models_1.DefaultPhysicalConstants;
    const problems = [
        ...rpmProblems(mphByRpm, rpmLimits),
        ...geometryProblems(v1Geometry),
        ...ballProblems(ball),
        ...environmentProblems(constants),
        ...(0, venues_1.venueProblems)(venue, sport),
    ];
    if (problems.length > 0) {
        return unsupported(problems.join("; "));
    }
    const mechanism = {
        mphByRpm: mphByRpm.slice(rpmLimits.minSafe, rpmLimits.maxSafe + 1),
        minRpm: rpmLimits.minSafe,
        maxRpm: rpmLimits.maxSafe,
        maxSpinRpm: rpmLimits.maxSpin,
        speedDamping: adjust.speedDampingFactor,
        spinDamping: adjust.spinDampingFactor,
        deflection: adjust.deflectionCorrection,
    };
    return {
        ok: true,
        profile: {
            trainer: {
                model,
                geometry: trainerGeometry(v1Geometry),
                mechanism: (0, mechanism_1.threeWheelMechanism)(mechanism),
            },
            ball,
            venue,
            environment: environmentFor(constants),
        },
    };
}
/**
 * A venue the profile can hold: the caller's own if it is already deeply
 * frozen (as every built-in venue is), otherwise a frozen copy, so the
 * caller's object is never frozen or kept. Undefined if it is not plain data.
 */
function frozenCopy(venue) {
    if ((0, freeze_1.isDeepFrozen)(venue)) {
        return venue;
    }
    try {
        return (0, freeze_1.deepFreeze)((0, freeze_1.clonePlainData)(venue));
    }
    catch {
        return undefined;
    }
}
// ---------------------------------------------------------------------------
// Building
const copy = ({ x, y, z }) => ({ x, y, z });
function trainerGeometry(geometry) {
    return {
        liftPivot: copy(geometry.liftPivotPosition),
        liftArmLength: geometry.liftArmLength,
        launchOriginOffset: copy(geometry.launchOriginOffset),
        launchRadius: geometry.launchDistanceFromRotationCenter,
        cameraOffsetFromHead: copy(geometry.cameraOffsetFromHead),
        cameraDownwardPitch: geometry.cameraDownwardPitch,
    };
}
function environmentFor(constants) {
    return { gravity: constants.gravity, airDensity: constants.airDensity };
}
// ---------------------------------------------------------------------------
// Validation: each returns what is wrong, empty when nothing is.
const isFiniteVec = (v) => [v.x, v.y, v.z].every(Number.isFinite);
const isPositive = (n) => Number.isFinite(n) && n > 0;
const failing = (checks) => checks.filter(([ok]) => !ok).map(([, problem]) => problem);
function rpmProblems(mphByRpm, limits) {
    const { minSafe, maxSafe, maxSpin } = limits;
    const bandProblems = failing([
        [Number.isInteger(minSafe) && minSafe >= 0, `safe minimum RPM ${minSafe} must be a whole number >= 0`],
        [Number.isInteger(maxSafe) && maxSafe > minSafe, `safe maximum RPM ${maxSafe} must be a whole number above the minimum`],
        [maxSafe < mphByRpm.length, `RPM-to-speed data ends at ${mphByRpm.length - 1} RPM, below the safe maximum ${maxSafe}`],
        [isPositive(maxSpin), `maximum spin RPM ${maxSpin} must be positive`],
    ]);
    if (bandProblems.length > 0) {
        return bandProblems;
    }
    const band = mphByRpm.slice(minSafe, maxSafe + 1);
    const increasing = band.every((mph, i) => Number.isFinite(mph) && mph >= 0 && (i === 0 || mph > band[i - 1]));
    return failing([
        [increasing, `speeds over the safe band ${minSafe}-${maxSafe} RPM must be finite and strictly increasing`],
    ]);
}
function geometryProblems(geometry) {
    return failing([
        [isFiniteVec(geometry.liftPivotPosition), "lift pivot must be finite"],
        [isPositive(geometry.liftArmLength), `lift arm length ${geometry.liftArmLength} must be positive`],
        [isFiniteVec(geometry.launchOriginOffset), "launch origin offset must be finite"],
        [
            isPositive(geometry.launchDistanceFromRotationCenter),
            `launch radius ${geometry.launchDistanceFromRotationCenter} must be positive`,
        ],
        [isFiniteVec(geometry.cameraOffsetFromHead), "camera offset must be finite"],
        [Number.isFinite(geometry.cameraDownwardPitch), "camera pitch must be finite"],
        // v1 measures head height from liftOrigin but places the head from
        // liftPivotPosition; the engine keeps only the pivot, so they must agree.
        [
            geometry.liftOrigin.z === geometry.liftPivotPosition.z,
            `lift origin height ${geometry.liftOrigin.z} must equal the pivot height ${geometry.liftPivotPosition.z}`,
        ],
        // Likewise v1 converts launch heights with defaultLaunchPointOffset,
        // which the engine derives from launchOriginOffset.
        [
            geometry.defaultLaunchPointOffset.z === geometry.launchOriginOffset.z,
            `default launch point height ${geometry.defaultLaunchPointOffset.z} must equal the launch origin height ${geometry.launchOriginOffset.z}`,
        ],
    ]);
}
function ballProblems(ball) {
    return failing([
        [isPositive(ball.diameter), `ball diameter ${ball.diameter} must be positive`],
        [isPositive(ball.mass), `ball mass ${ball.mass} must be positive`],
        [Number.isFinite(ball.dragCoefficient) && ball.dragCoefficient >= 0, `ball drag coefficient ${ball.dragCoefficient} must be >= 0`],
    ]);
}
function environmentProblems(constants) {
    return failing([
        [isPositive(constants.gravity), `gravity ${constants.gravity} must be positive`],
        [Number.isFinite(constants.airDensity) && constants.airDensity >= 0, `air density ${constants.airDensity} must be >= 0`],
    ]);
}
